import type { FormEvent, ReactNode } from "react";
import classNames from "classnames";
import type { TaskType } from "@common/types/task";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { Drawer } from "@design-system/Drawer";
import styles from "../CloseTemplateTasksDrawer.module.scss";
import { hasFullscreenPreview } from "./templateTaskPreview";

interface TemplateTaskDrawerShellProps {
    open: boolean;
    title: string;
    submitLabel: string;
    taskType: TaskType | null;
    preview: ReactNode;
    isSubmitDisabled: boolean;
    commandBarStart?: ReactNode;
    onClose: () => void;
    onSubmit: (event?: FormEvent<HTMLFormElement>) => void;
    children: ReactNode;
}

export const TemplateTaskDrawerShell = ({
    open,
    title,
    submitLabel,
    taskType,
    preview,
    isSubmitDisabled,
    commandBarStart,
    onClose,
    onSubmit,
    children,
}: TemplateTaskDrawerShellProps) => {
    const isFullscreen = hasFullscreenPreview(taskType);

    return (
        <Drawer
            open={open}
            onClose={onClose}
            direction="right"
            size={isFullscreen ? "fullScreen" : "quarter"}
            noPadding
            headerAddonLeft={title}
            hasBackdrop
        >
            <div className={styles.addTaskDrawerWrapper}>
                {preview}
                <div
                    className={classNames(styles.addTaskDrawerLeftForm, {
                        [styles.fullWidth]: !isFullscreen,
                        [styles.quarterWidth]: isFullscreen,
                    })}
                >
                    <form
                        onSubmit={onSubmit}
                        className={styles.addTaskDrawerContentWrapper}
                    >
                        {children}
                        <CommandIsland
                            sticky
                            className={
                                commandIslandStyles.bulkActionsStickyFooter
                            }
                        >
                            {commandBarStart}
                            <WithHotkey
                                hotKey="mod+enter"
                                hotKeyOptions={{
                                    description: `${submitLabel} task`,
                                    preventDefault: true,
                                    enableOnFormTags: true,
                                }}
                                hotKeyDisplayPosition="inline"
                            >
                                {(hotkeyHint) => (
                                    <Button
                                        variant="primary"
                                        size="md"
                                        type="submit"
                                        className="ml-auto"
                                        disabled={isSubmitDisabled}
                                        rightIcon={hotkeyHint}
                                    >
                                        {submitLabel}
                                    </Button>
                                )}
                            </WithHotkey>
                        </CommandIsland>
                    </form>
                </div>
            </div>
        </Drawer>
    );
};
